import { useCallback, useEffect, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { useDispatch } from 'react-redux'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { PageArea } from '..'
import Header from './components/Header'
import Loading from '../../components/process-state-notifications/Loading'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import { useRoleGridOptions } from './gridOptions'
import { useRoleData } from './hooks/useRoleData'
import { useContextMenu } from './hooks/useContextMenu'
import { RoleRow } from './model/RoleRow'
import { requireSave, doNotRequireSave } from '../../../store/requiredSaveData'
import { RowDragEvent } from 'ag-grid-community'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useColumnState } from '../../containers/BulkSheetView/hooks/columnState/useColumnState'
import {
  InputError,
  validateNodes,
} from '../../containers/BulkSheetView/lib/validation'
import { getRowNumber } from '../../containers/BulkSheetView/lib/gridApi'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { intl } from '../../../i18n'
import _ from 'lodash'
import { usePermissionPolicyOptions } from './hooks/usePermissionPolicyOptions'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useDragStyle } from '../../containers/BulkSheetView/hooks/gridEvents/rowDrag'

type Props = {
  uuid: string
}

const Roles = ({ uuid: functionUuid }: Props) => {
  const dispatch = useDispatch()
  const gridRef = useRef<AgGridReact<RoleRow>>(null)
  const bulkSheetRef = useRef<HTMLDivElement>(null)

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteTargetUuids, setDeleteTargetUuids] = useState<string[]>([])

  const {
    data,
    loading,
    hasChanged,
    fetchRoles,
    saveRoles,
    addRow,
    deleteRows,
    setDataAfterEditing,
  } = useRoleData(functionUuid)

  const {
    initialized: permissionPolicyOptionsInitialized,
    allPermissionPolicyOptions,
    attachedByDefaultPermissionPolicies,
    attachablePermissionPolicyOptions,
  } = usePermissionPolicyOptions()

  const gridOptions = useRoleGridOptions(attachablePermissionPolicyOptions)
  const bulkSheetState = useBulkSheetState(
    functionUuid,
    UiStateKey.RoleColumnAndFilterState
  )
  const columnState = useColumnState({ gridOptions, bulkSheetState })

  const handleReload = useCallback(() => {
    if (hasChanged()) {
      setCancelDialogOpen(true)
    } else {
      gridOptions.context = {
        ...gridOptions.context,
        errors: new InputError(),
      }
      fetchRoles()
    }
  }, [hasChanged, fetchRoles, gridOptions])

  const validateBeforeSubmit = useCallback(
    (rows: RoleRow[]) => {
      const targetRowUuids = rows
        .filter(row => row.added || row.edited)
        .map(row => row.uuid)
      const errors = validateNodes({
        gridApi: gridOptions.api,
        columnApi: gridOptions.columnApi,
        uuids: targetRowUuids,
        extraValidate: (colId, node, colDef) => {
          if (!node || !colId) return
          const validate = colDef.cellRendererParams?.validate
          if (!validate || !gridOptions.api) return
          const val = gridOptions.api.getValue(colId, node)
          return validate(val, {
            node,
            data: node.data,
            colDef,
            colId,
            api: gridOptions.api,
          })
        },
      })
      gridOptions.context = {
        ...gridOptions.context,
        errors,
      }
      return errors
    },
    [gridOptions]
  )

  const handleSave = useCallback(async () => {
    gridOptions.api?.stopEditing()

    const errors = validateBeforeSubmit(data)
    if (errors.hasMessage()) {
      dispatch(
        addGlobalMessage({
          type: MessageLevel.WARN,
          title: intl.formatMessage({ id: 'global.warning.businessError' }),
          text: errors.toMessage(id => {
            const node = gridOptions.api?.getRowNode(id)
            return !!node ? getRowNumber(node).toString() : ''
          }),
        })
      )
      return
    }

    await saveRoles()
    dispatch(doNotRequireSave())
  }, [saveRoles, dispatch, validateBeforeSubmit, data, gridOptions])

  const handleAddRow = useCallback(() => {
    addRow(attachedByDefaultPermissionPolicies)
    dispatch(requireSave())
  }, [addRow, dispatch, attachedByDefaultPermissionPolicies])

  const handleDeleteRequest = useCallback((uuids: string[]) => {
    setDeleteTargetUuids(uuids)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    deleteRows(deleteTargetUuids)
    setDeleteDialogOpen(false)
    setDeleteTargetUuids([])
    dispatch(requireSave())
  }, [deleteTargetUuids, deleteRows, dispatch])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
    setDeleteTargetUuids([])
  }, [])

  const handleCancelConfirm = useCallback(() => {
    setCancelDialogOpen(false)
    fetchRoles()
  }, [fetchRoles])

  const handleCancelClose = useCallback(() => {
    setCancelDialogOpen(false)
  }, [])

  const { getContextMenuItems } = useContextMenu({
    addRowAction: handleAddRow,
    deleteRowAction: handleDeleteRequest,
  })

  const dragStyle = useDragStyle(gridOptions)

  const onRowDragEnd = useCallback(
    (event: RowDragEvent<RoleRow>) => {
      const movedIds = event.nodes.map(v => v.id)
      const overNode = event.overNode
      try {
        if (!overNode || movedIds.includes(overNode.id)) {
          return
        }
        const result = data.filter(v => !movedIds.includes(v.uuid))
        const moved = data.filter(v => movedIds.includes(v.uuid))
        // If moved toward down, move after overNode.
        const index =
          result.findIndex(v => v.uuid === overNode.id!) +
          (event.overIndex > (event.node.rowIndex || 0) ? 1 : 0)
        result.splice(index, 0, ...moved)
        setDataAfterEditing(result)
      } finally {
        // Clear drag cell style
        dragStyle.refreshDragStyle()
      }
    },
    [data, dragStyle, setDataAfterEditing]
  )

  const onRowDataUpdated = useCallback((event: any) => {
    event.api?.refreshCells({ columns: ['rowNumber'], force: true })
  }, [])

  const onGridReady = useCallback(() => {
    if (!_.isEmpty(bulkSheetState.columnState) && gridOptions.columnApi) {
      gridOptions.columnApi.applyColumnState({
        state: bulkSheetState.columnState,
        applyOrder: true,
      })
    }
  }, [bulkSheetState.columnState, gridOptions.columnApi])

  const onFirstDataRendered = useCallback(() => {
    if (!gridOptions.api) return

    if (!_.isEmpty(bulkSheetState.filterState)) {
      gridOptions.api.setFilterModel(bulkSheetState.filterState)
    }
    bulkSheetState.finishRestoring()
  }, [bulkSheetState, gridOptions.api])

  useEffect(() => {
    if (!bulkSheetState.initialized) return
    fetchRoles()
  }, [bulkSheetState.initialized, fetchRoles])

  useEffect(() => {
    if (hasChanged()) {
      dispatch(requireSave())
    }
  }, [data, hasChanged, dispatch])

  const getDeleteRowLabels = useCallback(() => {
    return deleteTargetUuids
      .map(uuid => {
        const row = data.find(r => r.uuid === uuid)
        return row?.name || ''
      })
      .filter(name => name !== '')
  }, [deleteTargetUuids, data])

  useKeyBind(
    [{ key: KEY_SAVE, fn: handleSave, stopDefaultBehavior: true }],
    [handleSave]
  )

  return (
    <PageArea>
      <Header
        loading={loading}
        onReload={handleReload}
        onAddRow={handleAddRow}
        onSave={handleSave}
      />
      {bulkSheetState.initialized && permissionPolicyOptionsInitialized && (
        <BulkSheetView
          ref={bulkSheetRef}
          gridRef={gridRef}
          gridOptions={gridOptions}
          rowData={data}
          getContextMenuItems={getContextMenuItems}
          onGridReady={onGridReady}
          onRowDataUpdated={onRowDataUpdated}
          onFirstDataRendered={onFirstDataRendered}
          onColumnVisible={columnState.onColumnVisible}
          onColumnResized={columnState.rememberColumnState}
          onColumnMoved={columnState.rememberColumnState}
          onFilterChanged={columnState.onFilterChanged}
          onSortChanged={columnState.onSortChanged}
          onRowDragEnter={dragStyle.onRowDragEnter}
          onRowDragMove={dragStyle.onRowDragMove}
          onRowDragEnd={onRowDragEnd}
          onRowDragLeave={dragStyle.refreshDragStyle}
        />
      )}
      <Loading isLoading={loading} elem={bulkSheetRef.current} />
      <CancelConfirmDialog
        open={cancelDialogOpen}
        onConfirm={handleCancelConfirm}
        onClose={handleCancelClose}
      />
      {deleteDialogOpen && (
        <DeleteRowConfirmationDialog
          target={getDeleteRowLabels()}
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteCancel}
        />
      )}
    </PageArea>
  )
}

export default Roles
